import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Server,
  LayoutGrid,
  Package,
  Sparkles,
  Save,
  Mail,
  ChevronDown,
  Check,
  Info,
  MapPin,
  Building2,
  Plus,
  Minus as MinusIcon,
  Infinity as InfinityIcon,
  ShieldCheck,
} from 'lucide-react';
import Header from '../components/Header';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import {
  CORE_TIERS,
  CORE_INCLUDED_FEATURES,
  CORE_ADDONS,
  CORE_FEATURE_MATRIX,
  REGIONS,
  CORE_SAMPLE_CUSTOMERS,
} from '../mock/coreData';

const SectionCard = ({ children, className = '' }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7 ${className}`}>
    {children}
  </div>
);

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50">
      <Icon className="w-3.5 h-3.5 text-white/40" />
      {label}
    </div>
    <div className="mt-3 text-2xl font-semibold text-white tabular-nums">
      {typeof value === 'number' ? value.toLocaleString('en-US') : value}
    </div>
  </div>
);

const Dropdown = ({ label, icon: Icon, value, options, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const current = options.find((o) => o.id === value);
  return (
    <div ref={ref} className="relative">
      <div className="text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50 mb-2 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full h-11 px-4 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-white flex items-center justify-between text-[14px] transition-colors"
      >
        <span className={current ? 'text-white' : 'text-white/40'}>
          {current ? current.name : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 rounded-lg bg-[#12121a] border border-white/10 shadow-2xl overflow-hidden z-30 max-h-64 overflow-y-auto">
          {options.map((o) => (
            <button
              key={o.id}
              onClick={() => { onChange(o.id); setOpen(false); }}
              className={`w-full text-left flex items-center justify-between px-4 py-2.5 text-[13.5px] transition-colors ${
                o.id === value ? 'bg-white/5 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
            >
              <span>{o.name}</span>
              {o.id === value && <Check className="w-3.5 h-3.5 text-emerald-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TierCardCore = ({ tier, selected, onSelect }) => {
  const { formatMoney } = useCurrency();
  return (
    <button
      type="button"
      onClick={() => onSelect(tier.id)}
      className={`relative text-left rounded-xl p-5 border transition-all duration-200 group
        ${selected
          ? 'border-purple-400/60 bg-purple-400/[0.05] shadow-[0_0_0_1px_rgba(168,85,247,0.35),0_10px_40px_-10px_rgba(168,85,247,0.30)]'
          : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
        }`}
    >
      {selected && (
        <span className="absolute top-3 right-3 h-5 w-5 rounded-full bg-purple-400 text-black flex items-center justify-center">
          <Check className="w-3 h-3" strokeWidth={3} />
        </span>
      )}
      {tier.popular && !selected && (
        <span className="absolute -top-2 right-3 text-[9px] tracking-[0.18em] font-semibold uppercase px-2 py-0.5 rounded-full bg-purple-400/90 text-black">
          Most popular
        </span>
      )}
      <div className="text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50">
        {tier.label}
      </div>
      <div className="mt-3 text-xl md:text-[22px] leading-tight font-semibold text-white">
        {tier.name}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-white text-xl font-semibold tracking-tight">
          {formatMoney(tier.price)}
        </span>
        <span className="text-white/50 text-xs">one-time</span>
      </div>
      <div className="mt-1 text-[11px] text-white/45">
        + {formatMoney(tier.amcAmount)}/yr AMC (20%)
      </div>
      <div className="mt-4 space-y-1 text-[12px] text-white/65">
        <div className="flex items-center gap-2">
          <Server className="w-3 h-3 text-purple-300" />
          {typeof tier.tags === 'number' ? tier.tags.toLocaleString('en-US') : (
            <span className="inline-flex items-center gap-1 text-emerald-300"><InfinityIcon className="w-3 h-3" /> Unlimited</span>
          )} tags
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-3 h-3 text-purple-300" />
          {tier.webClients} web {tier.webClients === 1 ? 'client' : 'clients'}
        </div>
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-white/55">{tier.tagline}</p>
    </button>
  );
};

const AddonRow = ({ addon, qty, onQty }) => {
  const { formatMoney } = useCurrency();
  const active = qty > 0;
  return (
    <div className={`rounded-xl border p-4 transition-colors ${
      active ? 'border-purple-400/50 bg-purple-400/[0.04]' : 'border-white/10 bg-white/[0.02]'
    }`}>
      <div className="flex items-start gap-4">
        <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
          <Package className="w-4 h-4 text-purple-300" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-[14.5px]">{addon.name}</div>
          <div className="mt-0.5 text-xs text-white/55">{addon.description}</div>
          <div className="mt-2 text-[13px] text-white/85 tabular-nums">
            {addon.onRequest ? (
              <span className="text-emerald-300 font-semibold">On-Request</span>
            ) : (
              <>
                <span className="font-semibold">{formatMoney(addon.price)}</span>
                <span className="text-white/50 text-[11px] uppercase tracking-wider ml-1"> / {addon.unit}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onQty(Math.max(0, qty - 1))}
            disabled={qty === 0}
            className="h-8 w-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.10] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white flex items-center justify-center transition-colors"
          >
            <MinusIcon className="w-3.5 h-3.5" />
          </button>
          <div className="w-10 text-center text-white font-semibold tabular-nums">{qty}</div>
          <button
            onClick={() => onQty(Math.min(addon.maxQty, qty + 1))}
            disabled={qty >= addon.maxQty}
            className="h-8 w-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.10] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white flex items-center justify-center transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CorePricing = () => {
  const { formatMoney } = useCurrency();
  const { user } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState('grow');
  const [region, setRegion] = useState('india');
  const [customer, setCustomer] = useState('');
  const [addonQty, setAddonQty] = useState(() =>
    CORE_ADDONS.reduce((acc, a) => ({ ...acc, [a.id]: 0 }), {})
  );

  const tier = CORE_TIERS.find((t) => t.id === selectedTier);
  // Re-brand "Itanta X" tier names to the active brand's prefix.
  const rebrandName = (name) => name.replace(/^Itanta\s/, `${brand.tierPrefix} `);
  const brandedTiers = useMemo(
    () => CORE_TIERS.map((t) => ({ ...t, name: rebrandName(t.name) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand.tierPrefix],
  );
  const brandedTier = brandedTiers.find((t) => t.id === selectedTier);
  const customerObj = CORE_SAMPLE_CUSTOMERS.find((c) => c.id === customer);

  // Auto-set region from customer selection
  React.useEffect(() => {
    if (customerObj && customerObj.regionId !== region) {
      setRegion(customerObj.regionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const customersForRegion = CORE_SAMPLE_CUSTOMERS.filter((c) => c.regionId === region);

  const addonsTotal = useMemo(() => {
    return CORE_ADDONS.reduce((sum, a) => {
      if (a.onRequest) return sum;
      return sum + (addonQty[a.id] || 0) * a.price;
    }, 0);
  }, [addonQty]);

  const licenseTotal = tier.price;
  const amcTotal = tier.amcAmount;
  const grandTotal = licenseTotal + addonsTotal + amcTotal;

  const resetAll = () => {
    setSelectedTier('grow');
    setAddonQty(CORE_ADDONS.reduce((acc, a) => ({ ...acc, [a.id]: 0 }), {}));
  };

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white relative overflow-x-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1200px 500px at 15% -10%, rgba(168,85,247,0.10), transparent 60%), radial-gradient(1000px 600px at 90% 5%, rgba(34,211,238,0.10), transparent 60%)',
        }}
      />

      <Header />

      <main className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {/* Hero */}
        <section className="pt-14 md:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-400" />
            <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">
              {brand.tierPrefix} Core · Perpetual License
            </span>
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl">
            Configure {brand.tierPrefix} Core{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(90deg, #a855f7 0%, #22d3ee 100%)' }}
            >
              for your customer.
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/60 leading-relaxed text-[15px]">
            Pick a tier, add optional modules, and get an instant quote. Region &amp; customer-specific price lists
            apply automatically when assigned.
          </p>
        </section>

        {/* Region + Customer selectors */}
        <SectionCard className="mt-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Dropdown
              label="Region"
              icon={MapPin}
              value={region}
              options={REGIONS}
              onChange={(v) => { setRegion(v); setCustomer(''); }}
              placeholder="Select region"
            />
            <Dropdown
              label="Customer"
              icon={Building2}
              value={customer}
              options={customersForRegion.length ? customersForRegion : [{ id: '', name: 'No customers in this region yet' }]}
              onChange={setCustomer}
              placeholder="Select customer (optional)"
            />
          </div>
          <div className="mt-4 flex items-start gap-3 rounded-lg bg-white/[0.02] border border-white/10 px-4 py-3">
            <Info className="w-4 h-4 text-cyan-300 mt-0.5 flex-shrink-0" />
            <p className="text-[12.5px] text-white/60 leading-relaxed">
              {customerObj ? (
                <>
                  <span className="text-white/80 font-semibold">{customerObj.name}</span> — default price list applies.{' '}
                  <span className="text-white/45">Custom price lists can be uploaded by Admin/Users from the Admin panel.</span>
                </>
              ) : (
                <>Region-specific list prices &amp; discounts (uploaded by Admin/Users) will be applied here. <span className="text-white/45">Currently showing default USD pricing from the 2026 brochure.</span></>
              )}
            </p>
          </div>
        </SectionCard>

        {/* Main grid */}
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 lg:gap-8">
          <div className="space-y-6">
            {/* Choose tier */}
            <SectionCard>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-semibold text-white">Choose a license tier</h2>
                <button
                  onClick={resetAll}
                  className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 hover:text-white transition-colors"
                >
                  Reset all
                </button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {brandedTiers.map((t) => (
                  <TierCardCore
                    key={t.id}
                    tier={t}
                    selected={selectedTier === t.id}
                    onSelect={setSelectedTier}
                  />
                ))}
              </div>

              {/* Stat tiles */}
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile icon={Server} label="Data tags" value={tier.tags} />
                <StatTile icon={Users} label="Web clients" value={tier.webClients} />
                <StatTile icon={LayoutGrid} label="Modules included" value={CORE_INCLUDED_FEATURES.length} />
                <StatTile icon={ShieldCheck} label="AMC / year" value={formatMoney(tier.amcAmount)} />
              </div>

              {/* Included features */}
              <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-3">
                  Included in every tier
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {CORE_INCLUDED_FEATURES.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-[13px] text-white/80">
                      <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>

            {/* Optional Add-ons */}
            <SectionCard>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-white">Optional add-on modules</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  One-time · per license
                </div>
              </div>
              <div className="space-y-3">
                {CORE_ADDONS.map((a) => (
                  <AddonRow
                    key={a.id}
                    addon={a}
                    qty={addonQty[a.id]}
                    onQty={(q) => setAddonQty({ ...addonQty, [a.id]: q })}
                  />
                ))}
              </div>
            </SectionCard>

            {/* AMC note */}
            <SectionCard>
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-lg bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-300" />
                </span>
                <div>
                  <div className="text-white font-semibold">20% Annual Maintenance Contract — Mandatory</div>
                  <div className="text-[12.5px] text-white/60 mt-0.5">
                    Bug fixes &amp; platform updates · Security patches · Priority upgrades · Refresher training (2 hrs)
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Right: live estimate */}
          <div>
            <aside className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-6 md:p-7 sticky top-24">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">
                    Live estimate
                  </div>
                  <div className="mt-2 text-[22px] font-semibold text-white">{tier.name}</div>
                  {customerObj && (
                    <div className="mt-1 text-[12px] text-white/50">For {customerObj.name}</div>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute inset-0 blur-2xl bg-purple-400/30 rounded-full" />
                  <Sparkles className="relative w-6 h-6 text-purple-300" />
                </div>
              </div>

              {/* License */}
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">
                  License (one-time)
                </div>
                <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                  <span
                    className="text-[42px] leading-none font-bold tracking-tight bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(90deg, #a855f7 0%, #22d3ee 100%)' }}
                  >
                    {formatMoney(licenseTotal)}
                  </span>
                </div>
              </div>

              {/* AMC */}
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">
                  Annual maintenance
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold text-white tracking-tight">
                    {formatMoney(amcTotal)}
                  </span>
                  <span className="text-white/50 text-sm">/yr · 20%</span>
                </div>
              </div>

              {/* Breakdown */}
              <div className="mt-6 space-y-2 text-[13px]">
                <div className="flex items-center justify-between text-white/70 gap-3">
                  <span>Base license · {brandedTier.name.replace(/^(Itanta|Streamline)\s/, '')}</span>
                  <span className="text-white/90 tabular-nums whitespace-nowrap">{formatMoney(licenseTotal)}</span>
                </div>
                {CORE_ADDONS.filter((a) => addonQty[a.id] > 0 && !a.onRequest).map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-white/70 gap-3">
                    <span className="min-w-0 truncate">+ {addonQty[a.id]}× {a.name}</span>
                    <span className="text-white/90 tabular-nums whitespace-nowrap">
                      {formatMoney(addonQty[a.id] * a.price)}
                    </span>
                  </div>
                ))}
                {CORE_ADDONS.filter((a) => addonQty[a.id] > 0 && a.onRequest).map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-white/70 gap-3">
                    <span className="min-w-0 truncate">+ {addonQty[a.id]}× {a.name}</span>
                    <span className="text-emerald-300 tabular-nums whitespace-nowrap">On-Request</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-white/70 gap-3 pt-2 border-t border-white/5">
                  <span>Annual maintenance (20%)</span>
                  <span className="text-white/90 tabular-nums whitespace-nowrap">{formatMoney(amcTotal)}/yr</span>
                </div>
              </div>

              {/* Grand total */}
              <div className="mt-6 rounded-xl border border-purple-400/25 bg-purple-400/[0.05] p-4">
                <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-purple-200">
                  Grand total (Year 1)
                </div>
                <div className="mt-3 flex items-center justify-between text-[13px] text-white/70">
                  <span>License + Add-ons</span>
                  <span className="text-white tabular-nums">{formatMoney(licenseTotal + addonsTotal)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[13px] text-white/70">
                  <span>AMC (Year 1)</span>
                  <span className="text-white tabular-nums">{formatMoney(amcTotal)}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-purple-400/20 flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-white/80">Total to pay</span>
                  <span
                    className="text-[24px] font-bold tabular-nums bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(90deg, #a855f7 0%, #22d3ee 100%)' }}
                  >
                    {formatMoney(grandTotal)}
                  </span>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-5 grid grid-cols-[1fr_auto] gap-3">
                <button
                  onClick={() => {
                    const payload = {
                      product: 'core',
                      tier_id: selectedTier,
                      tier_name: tier.name,
                      tier_price: tier.price,
                      amc_amount: tier.amcAmount,
                      addons: CORE_ADDONS.filter((a) => addonQty[a.id] > 0).map((a) => ({
                        id: a.id, name: a.name, unit_price: a.onRequest ? 0 : a.price,
                        qty: addonQty[a.id], on_request: !!a.onRequest,
                      })),
                      region_code: (REGIONS.find((r) => r.id === region) || {}).id,
                      customer_hint: customerObj?.name || null,
                    };
                    if (!user) {
                      navigate('/login', { state: { from: { pathname: '/admin/quotes/new' } } });
                    } else {
                      navigate('/admin/quotes/new', { state: payload });
                    }
                  }}
                  className="h-11 rounded-lg bg-purple-400 hover:bg-purple-300 text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-[0_10px_30px_-10px_rgba(168,85,247,0.6)]"
                >
                  <Save className="w-4 h-4" />
                  Build quotation
                </button>
                <button className="h-11 px-4 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                  <Mail className="w-4 h-4" />
                  Email
                </button>
              </div>
            </aside>
          </div>
        </section>

        {/* Feature matrix */}
        <section className="mt-16 md:mt-24">
          <div className="text-[11px] tracking-[0.22em] font-semibold uppercase text-purple-300">
            Feature access
          </div>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold text-white tracking-tight">
            What's included in each tier
          </h2>
          <p className="mt-3 max-w-2xl text-white/60 leading-relaxed">
            Every tier ships with the core platform features. Star-marked capabilities are optional paid add-ons.
          </p>

          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-6 py-5 text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50 w-[36%]">
                      Feature
                    </th>
                    {brandedTiers.map((t) => {
                      const isSel = selectedTier === t.id;
                      return (
                        <th key={t.id} className={`text-left px-5 py-5 ${isSel ? 'bg-purple-400/[0.06]' : ''}`}>
                          <div className={`text-[10px] tracking-[0.2em] font-semibold uppercase ${isSel ? 'text-purple-200' : 'text-white/50'}`}>
                            {t.label}
                          </div>
                          <div className={`mt-2 text-lg font-semibold ${isSel ? 'text-purple-200' : 'text-white'}`}>
                            {t.name.replace(/^(Itanta|Streamline)\s/, '')}
                          </div>
                          <div className="mt-1 text-xs text-white/50">{formatMoney(t.price)}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {CORE_FEATURE_MATRIX.map((row, i) => (
                    <tr key={i} className={`border-b border-white/5 ${i % 2 === 1 ? 'bg-white/[0.015]' : ''}`}>
                      <td className="px-6 py-4 text-white/75">{row.feature}</td>
                      {['start', 'grow', 'scale', 'infinity'].map((tid) => {
                        const isSel = selectedTier === tid;
                        const v = row[tid];
                        return (
                          <td key={tid} className={`px-5 py-4 ${isSel ? 'bg-purple-400/[0.04]' : ''}`}>
                            {v === 'included' && (
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/15 border border-emerald-400/40">
                                <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={3} />
                              </span>
                            )}
                            {v === 'addon' && (
                              <span className="inline-flex items-center gap-1.5 text-purple-200 text-[12.5px]">
                                <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                                Add-on
                              </span>
                            )}
                            {v === '—' && <span className="text-white/25">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[12px] text-white/40">
            © {new Date().getFullYear()} {brand.companyLegalName} · Core Pricing Estimator
          </div>
          <div className="flex items-center gap-6 text-[12px] text-white/40">
            <a href="#" className="hover:text-white/70 transition-colors">Terms</a>
            <a href="#" className="hover:text-white/70 transition-colors">Privacy</a>
            <a href="#" className="hover:text-white/70 transition-colors">Contact</a>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default CorePricing;
